import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { BatchDetailRecord, BatchRecord, BatchStatus, IsoDate, NumericString } from '@henlytics/types'
import { api } from '../lib/api-client'
import { queryKeys } from '../lib/query-keys'
import { clientUuid } from '../lib/uuid'

export function useBatches() {
  return useQuery({
    queryKey: queryKeys.batches,
    queryFn: ({ signal }) => api.get<BatchRecord[]>('/batches', signal),
  })
}

export function useBatch(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.batch(batchId ?? ''),
    queryFn: ({ signal }) => api.get<BatchDetailRecord>(`/batches/${batchId}`, signal),
    enabled: Boolean(batchId),
  })
}

export interface NewBatch {
  code: string
  breed?: string | null
  supplier?: string | null
  arrivalDate: IsoDate
  chicksReceived: number
  chickAgeDaysOnArrival: number
  chickTotalCost: NumericString
  chickUnitCost?: NumericString | null
  layerStartDateOverride?: IsoDate | null
  /** Pass a stable id if the form can be retried, so a retry is a no-op (§3.2). */
  clientUuid?: string
  arrivalEventClientUuid?: string
}

export function useCreateBatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: NewBatch) =>
      api.post<BatchRecord>('/batches', {
        ...input,
        clientUuid: input.clientUuid ?? clientUuid(),
        // The batch row and its arrival ledger row are two facts, two ids.
        arrivalEventClientUuid: input.arrivalEventClientUuid ?? clientUuid(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.batches })
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports })
    },
  })
}

export function useUpdateBatch(batchId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: { code?: string; breed?: string | null; supplier?: string | null; status?: BatchStatus; layerStartDateOverride?: IsoDate | null }) =>
      api.patch<BatchRecord>(`/batches/${batchId}`, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.batches })
      void queryClient.invalidateQueries({ queryKey: queryKeys.batch(batchId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports })
    },
  })
}
